import { call, put, delay, fork, takeLatest, takeLeading, select } from "redux-saga/effects";
import { AuthAPI } from "services/auth";
import { push, replace } from "connected-react-router";
import { TabName } from "const";
import { Routes } from "const/routes";
import { removeToken, setToken } from "utils/common-util";
import {
  signIn,
  signInSuccess,
  signInFailure,
  signOut,
  terminated,
  hideAllMainMenu,
  flagSsoError,
} from "redux/reducers/auth";
import { markUserIsUHNW, getProfileSuccess, setRegisteredStatus, markUserIsPWM } from "redux/reducers/customer";
import { getProfileSaga, checkUserTypeSaga } from "./customer";
import { oauthBypassAcceptedDisclaimerProcess } from "redux/reducers/disclaimer";
import { getDisclaimerList } from "redux/reducers/disclaimer";
import { handleClientError } from "utils/saga-error";
import { isRmUser } from "redux/reducers/portfolio";
import { checkRegister } from "./disclaimer";
import { accept as acceptDisclaimer } from "redux/reducers/disclaimer";
import { CustomerSelector } from "redux/selector/customer-selector";
import { LoggingAPI } from "services/logging-server";
import { DeviceUtil } from "utils/device-util";
import { oAuthStorage } from "utils/o-auth-utils";

const oAuthChannel = {
  PWM_RM: "pwm_rm",
  Funds: "fundstrans",
  PrimaryProductList: "primary_product_list",
  StructureNoteProductList: "structure_note_product_list",
};

function authApiMapping(isSingleSignOn, program) {
  if (!isSingleSignOn) return AuthAPI.signIn;
  else
    return (
      {
        fundstrans: AuthAPI.oAuthFundTrans,
        pwm_rm: AuthAPI.oAuthPWM,
      }[program] ?? AuthAPI.oAuth
    );
}

function* signInSaga(action) {
  const { authType, program, channel: ssoChannel, ...payload } = action.payload;
  const isSingleSignOn = authType === "oauth";
  try {
    yield put(getDisclaimerList());

    const authEndpointAPI = authApiMapping(isSingleSignOn, program);
    const response = yield call(authEndpointAPI, payload);
    const responseData = response.data;

    const { tokenType, accessToken, redirect: deepLinkTarget, redirectSub, channel, param1 } = responseData;
    const token = `${tokenType} ${accessToken}`;
    setToken(token);
    yield put(signInSuccess({ token }));

    //* From Channel
    oAuthStorage.channel = channel ?? ssoChannel;

    //* Deep Link
    const useDeepLink = !!deepLinkTarget;
    if (useDeepLink) {
      let requestURL;
      let securityCode;
      let activeTabKey;
      switch (deepLinkTarget) {
        case "FUND":
          requestURL = Routes.UltraHigh.Portfolio.Fund;
          break;
        case "PRIMARY_PRODUCT_LIST":
          requestURL = Routes.Primary.Products;
          break;
        case "STRUCTURE_NOTE_PRODUCT_LIST":
          requestURL = Routes.StructureNote;
          break;
        case "BOND2ND":
          switch (redirectSub) {
            case "DETAIL":
              securityCode = param1;
              requestURL = Routes.Secondary.Products;
              break;
            case "LIST":
              requestURL = Routes.Secondary.Products;
              break;
            case "OUTSTANDING":
              requestURL = Routes.Secondary.Products;
              activeTabKey = TabName.MY_SECONDARY;
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
      oAuthStorage.deepLinkInfo = { requestURL, deepLinkTarget, securityCode, activeTabKey };
    }

    const notRequireProfile = program === oAuthChannel.Funds;
    if (notRequireProfile) {
      // already provide user's profile
      yield put(
        getProfileSuccess({
          thaiName: responseData.thaiName,
          engName: responseData.engName,
          age: responseData.age,
        }),
      );
    } else {
      const success = yield call(getProfileSaga);
      if (!success) return;
    }

    yield call(checkRegister);

    // check: user must accept currently disclaimer or not ?
    if (program !== oAuthChannel.PWM_RM) {
      // rm is not own this account
      const { acceptDisc } = yield select(CustomerSelector.userInfo);
      if (!acceptDisc) {
        // already accept this version
        yield put(acceptDisclaimer({ isSingleSignOn, skipLandingPage: !!program })); // skip disclaimer page
        const normalLoginOrNormalOAuth = !program;
        if (normalLoginOrNormalOAuth) return; // no reason to do below process (redirect page process is in above accept disclaimer action)
      }
    }

    //! TODO : maybe in future, there are case that user authenticate via oauth somehow
    //!        and require specific page to open but also require accept disclaimer first
    //!        that case is'nt support in currently.
    //!      : currently support is the case that specific page target (via 'program' param)
    //!        but must skip accept disclaimer page.

    // next page
    let targetPage = Routes.Disclaimer;
    const redirect = isSingleSignOn ? replace : push;
    if (program) {
      // request page from oauth
      yield put(oauthBypassAcceptedDisclaimerProcess());
      yield put(setRegisteredStatus(true));

      switch (program) {
        case oAuthChannel.PWM_RM:
          yield put(markUserIsUHNW());
          yield put(markUserIsPWM());
          yield call(checkUserTypeSaga);
          yield put(hideAllMainMenu());
          yield put(isRmUser());
          targetPage = Routes.UltraHigh.Portfolio.Home;
          break;
        case oAuthChannel.PrimaryProductList:
          yield call(checkUserTypeSaga);
          targetPage = Routes.Primary.Products;
          break;
        case oAuthChannel.StructureNoteProductList:
          yield call(checkUserTypeSaga);
          targetPage = Routes.StructureNote;
          break;
        default:
          break;
      }
    }
    yield put(redirect(targetPage));
  } catch (error) {
    console.log(error);
    yield put(replace(Routes.Error));
    yield put(signInFailure(handleClientError({ ...error, ignore: true })));
    if (isSingleSignOn) yield put(flagSsoError());

    yield LoggingAPI.error({
      authToken: payload.authToken,
      message: JSON.stringify({
        error: {
          message: error.message,
          error,
        },
        deviceInfo: DeviceUtil.getDeviceInfo(),
      }),
    });
  }
}

function* signOutSaga() {
  // don't switch line of redirect page
  // because router will change back to previous
  // auth route location block
  yield put(push(Routes.SignIn));

  try {
    yield fork(AuthAPI.signOut);
  } catch (error) {
    console.log(error);
  }
  yield delay(400);
  removeToken();
}

function* terminatedSaga() {
  removeToken();
  yield put(push(Routes.Terminated));
}

const saga = [
  takeLeading(signIn.type, signInSaga),
  takeLatest(signOut.type, signOutSaga),
  takeLatest(terminated.type, terminatedSaga),
];

export default saga;
